import API from '../../api/API';
import { DEVICE_FLAGS, ROUTES } from '../../constants';
import { SIGN_OUT_CODES } from '../../constants/auth.constants';
import { APPLICATION_STEPS } from '../../constants/applicationSteps';
import { navigate } from '../../services/navigation.service';
import userService from '../../services/user.service';
import { isMagicLinkUser } from '../selectors/user.selector';
import {
  APP_FINISH_STEP,
  AUTH_FINISH_ONBOARDING,
  AUTH_LOGIN,
  AUTH_LOGOUT,
  AUTH_UPDATE_TOKEN,
  ORGANIZATION_SWITCH,
  WORKSPACES_SELECT_WORKSPACE,
} from '../actions';
import { updateDeviceFlags } from '../actions/deviceFlags.actions';
import { updateStateToStorage } from '../actions/storage.actions';
import { attachUserId, fetchAuthenticatedUser } from '../actions/auth.actions';
import { updateOrganization } from '../actions/organization.actions';

export const authInit =
  ({ dispatch }) =>
  next =>
  action => {
    next(action);

    if (
      action.type === APP_FINISH_STEP &&
      action.payload?.step === APPLICATION_STEPS.PERSIST_STORAGE
    ) {
      // Attach region to API client based on persisted storage value
      API.setClientByRegion(userService.region);
      // Attach user ID
      dispatch(attachUserId({ id: userService.userId }));

      // Attach access token to API client if not expired
      if (!userService.isTokenExpired && userService.accessToken) {
        API.setToken(userService.accessToken);
      }

      // Initialize user service
      userService.init();
    }
  };

export const authUpdateToken = () => next => action => {
  if (action.type === AUTH_UPDATE_TOKEN) {
    API.setToken(action.payload.accessToken);
    API.setClientByRegion(userService.region);
  }

  next(action);
};

export const authLoginProcess =
  ({ dispatch }) =>
  next =>
  async action => {
    if (action.type === AUTH_LOGIN) {
      // Attach user ID
      dispatch(attachUserId({ id: userService.userId }));

      API.setToken(action.payload.accessToken);

      await dispatch(updateOrganization());

      next(action);
      dispatch(updateStateToStorage());
    } else {
      next(action);
    }
  };

const SESSION_EXPIRY_CODES = new Set([
  SIGN_OUT_CODES.TOKEN_REFRESH_FAILED,
  SIGN_OUT_CODES.SESSION_EXPIRED,
  SIGN_OUT_CODES.UNAUTHORIZED,
]);

export const authLogoutProcess =
  ({ dispatch, getState }) =>
  next =>
  action => {
    next(action);

    if (action.type === AUTH_LOGOUT) {
      // On session-expiry the biometric middleware may redirect to
      // AUTH_SESSION_EXPIRED so BiometricUnlockScreen can reuse the
      // existing identity. Keep state.auth.id intact in that case —
      // /challenge needs a non-empty userId. The reducer wipes id for
      // real logouts via initialState.
      const isSessionExpiry = SESSION_EXPIRY_CODES.has(action.payload?.signOutCode);
      if (!isSessionExpiry) {
        dispatch(attachUserId({ id: '' }));
      }
      API.setToken(null);
      dispatch(updateStateToStorage());
    }
  };

export const authOnboardingFinished =
  ({ dispatch, getState }) =>
  next =>
  action => {
    next(action);

    if (action.type === AUTH_FINISH_ONBOARDING) {
      dispatch(updateDeviceFlags({ key: DEVICE_FLAGS.ONBOARDING_FINISHED }));

      navigate(ROUTES.WELCOME);
    }
  };

export const authFetchAuthenticatedUser =
  ({ dispatch, getState }) =>
  next =>
  action => {
    next(action);
    // Pre-auth AUTH_UPDATE_USER (email attach on the sign-in screen) cascades
    // into a WORKSPACES_SELECT_WORKSPACE with no session yet — skip the
    // tokenless /user/me it would fire (the backend 401s it on every sign-in).
    if (!userService.isAuthenticated) return;
    if (
      action.type === ORGANIZATION_SWITCH ||
      (action.type === WORKSPACES_SELECT_WORKSPACE && !isMagicLinkUser(getState()))
    ) {
      dispatch(fetchAuthenticatedUser());
    }
  };

export const authMiddleware = [
  authInit,
  authLoginProcess,
  authUpdateToken,
  authLogoutProcess,
  authOnboardingFinished,
  authFetchAuthenticatedUser,
];
